import React from "react";
import { Switch, Tag, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

import "./ReturnProcessSelect.scss";

const OnOff = ({ value }) => (
  <Tag color={value ? "green" : "default"} className="return-process-default-tag">
    {value ? "On" : "Off"}
  </Tag>
);

/**
 * Per-order override of the client's Settings > Return Process, shown on the plan page so
 * internal users no longer have to log in as the client to change it for one order.
 *
 *  - "Return to pickup after all stops" is the billed one: the leg from the last stop back to
 *    the first pickup becomes the route's return distance / return time for both the client
 *    and the driver estimate. Off => 0 return distance, nothing billed.
 *  - "Return failed packages" is applied later, when the route finishes (the return stop to
 *    the Return Location is only created if a delivery actually failed). Its potential cost is
 *    previewed per route in the "If any failed stops" line.
 *
 * `undefined` values mean "not chosen yet": the parent seeds them from the client defaults
 * once those have loaded, so the switches always reflect what will actually be sent.
 */
const ReturnProcessSelect = ({
  returnToPickupEnabled,
  returnOnFailedEnabled,
  onChange,
  defaults,
  settings,
  loading,
  clientMissing
}) => {
  const toPickup = returnToPickupEnabled ?? defaults?.returnToPickupEnabled ?? false;
  const onFailed = returnOnFailedEnabled ?? defaults?.returnOnFailedEnabled ?? false;
  const returnAddress = settings?.address;

  return (
    <div className="return-process-select">
      <p className="location-title">
        Return Process
        <Tooltip
          overlayClassName="stop-ordering-tooltip"
          title="Defaults come from the client's Settings > Return Process. Changing them here only affects the routes created from this order."
        >
          <InfoCircleOutlined className="stop-ordering-info" />
        </Tooltip>
      </p>

      {clientMissing ? (
        <p className="return-process-hint">
          Select a client to load its Return Process defaults.
        </p>
      ) : (
        <div className="return-process-rows">
          <div className="return-process-row">
            <Switch
              checked={toPickup}
              loading={loading}
              onChange={(checked) =>
                onChange({
                  returnToPickupEnabled: checked,
                  returnOnFailedEnabled: onFailed
                })
              }
            />
            <div className="return-process-copy">
              <div className="return-process-label">
                Return to pickup (HQ) after all stops
                <span className="return-process-default">
                  client default: <OnOff value={!!defaults?.returnToPickupEnabled} />
                </span>
              </div>
              <div className="return-process-desc">
                Adds a return stop at the pickup location with all packages. The drive
                from the last stop back to the pickup is billed as return distance and
                return time on both the client and the driver estimate.
              </div>
            </div>
          </div>

          <div className="return-process-row">
            <Switch
              checked={onFailed}
              loading={loading}
              onChange={(checked) =>
                onChange({
                  returnToPickupEnabled: toPickup,
                  returnOnFailedEnabled: checked
                })
              }
            />
            <div className="return-process-copy">
              <div className="return-process-label">
                Return failed packages to the Return Location
                <span className="return-process-default">
                  client default: <OnOff value={!!defaults?.returnOnFailedEnabled} />
                </span>
              </div>
              <div className="return-process-desc">
                Only if a delivery fails: when the route finishes, a return stop with the
                undelivered packages is added
                {returnAddress ? (
                  <>
                    {" "}
                    to <b>{returnAddress}</b>
                  </>
                ) : (
                  " to the client's Return Location"
                )}
                . Not part of the estimate; the potential extra distance is shown per
                route under &ldquo;If any failed stops&rdquo;.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReturnProcessSelect;
